import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { readMe, generateTwoFactorSecret, enableTwoFactor } from "../providers/api.ts";
import { directus } from "@/providers/directus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useBrand } from "@/hooks/useBrand";

export function TfaSetup() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const me: any = await directus.request(readMe({ fields: ["tfa_secret"] as any }));
        // Same withheld-field fault as authProvider's needsTfaEnrollment, in the
        // opposite polarity, and this is the half that traps. A role that cannot read
        // tfa_secret gets 200 with the key absent, so `me?.tfa_secret` is undefined,
        // this guard does not fire, and the account is stuck on a page it can never
        // complete -- enrolling needs `update` on the same field it cannot read.
        //
        // "Cannot know" must therefore leave rather than stay: an account that cannot
        // read the field has no business on this screen. Roles that CAN read it are
        // unaffected, so staff enrolment behaves exactly as before.
        if (!me || !Object.prototype.hasOwnProperty.call(me, "tfa_secret") || me.tfa_secret) {
          navigate("/", { replace: true });
          return;
        }
      } catch {
        // Was `try/finally` with no catch, so a rejected request escaped as an
        // unhandled rejection and left the form on screen. Leaving is the same safe
        // default as above: if enrolment state cannot be established, this is not the
        // right screen to be on.
        navigate("/", { replace: true });
        return;
      } finally {
        setChecking(false);
      }
    })();
  }, [navigate]);

  async function handleGenerate(e: React.SyntheticEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await directus.request(generateTwoFactorSecret(password));
      setSecret(res.secret);
      setOtpauthUrl(res.otpauth_url);
    } catch {
      setError("Incorrect password.");
    } finally {
      setBusy(false);
    }
  }

  async function handleEnable(e: React.SyntheticEvent) {
    e.preventDefault();
    if (!secret) return;
    setError(null);
    setBusy(true);
    try {
      await directus.request(enableTwoFactor(secret, otp));
      navigate("/", { replace: true });
    } catch {
      setError("Incorrect code. Check the app and try again.");
    } finally {
      setBusy(false);
    }
  }

  if (checking) return null;

  return (
    <div className="login-page min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-[420px]">
        <div className="login-logo-shift flex items-center gap-2.5 mb-6 justify-center">
          <img src={brand.logoUrl} alt={brand.name} className="w-9 h-9 object-contain" />
          <div className="brand-wordmark text-xl">{brand.name}</div>
        </div>
        <div className="login-card rounded-lg p-6">
          <h1 className="text-sm font-medium mb-1">Set up two-factor authentication</h1>
          <p className="text-xs text-muted-foreground mb-4">
            Required for your account before you can continue.
          </p>

          {!secret ? (
            <form onSubmit={handleGenerate} className="flex flex-col gap-3">
              <label htmlFor="tfa-password" className="text-xs text-muted-foreground">
                Confirm your password
                <Input
                  id="tfa-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                />
              </label>
              {error && <div className="text-xs text-destructive">{error}</div>}
              <Button type="submit" disabled={busy || !password} className="mt-1">
                {busy ? "Generating..." : "Generate secret"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleEnable} className="flex flex-col gap-3">
              <div className="text-xs text-muted-foreground">
                Add this key to your authenticator app (Google Authenticator, 1Password, etc.):
              </div>
              <div className="font-mono text-xs break-all rounded border border-border bg-muted p-2">
                {secret}
              </div>
              {otpauthUrl && (
                <a
                  href={otpauthUrl}
                  className="text-xs text-muted-foreground underline break-all"
                >
                  {otpauthUrl}
                </a>
              )}
              <label htmlFor="tfa-otp" className="text-xs text-muted-foreground">
                Enter the 6-digit code from the app
                <Input
                  id="tfa-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="mt-1"
                />
              </label>
              {error && <div className="text-xs text-destructive">{error}</div>}
              <Button type="submit" disabled={busy || !otp} className="mt-1">
                {busy ? "Confirming..." : "Enable two-factor authentication"}
              </Button>
            </form>
          )}

          <div className="mt-4 pt-3 border-t border-border text-center">
            <a href="/logout" className="text-xs text-muted-foreground hover:text-foreground underline">
              Sign out / Use a different account
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
